import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { DEFAULT_PRODUCT_QUANTITY, GET_AVAILABILITY_STORES_TYPE } from '@utils/constant';
import useGetAvailabilityStores from '@utils/customsHook/useGetAvailabilityStores';
import {
  useConvertAdditionalOptionsActive,
  useMappingAdditionalOptions
} from '@utils/customsHook/useGetAvailabilityStores/useMappingAdditionalOptions';
import { useMappingFreeGiftData } from '@utils/customsHook/useGetAvailabilityStores/useMappingFreeGiftData';
import useMappingProductsList, {
  mappingProductsListPayload
} from '@utils/customsHook/useGetAvailabilityStores/useMappingProductsList';

export const useBOGetAvailabilityStores = () => {
  const mappingProductsList = useMappingProductsList();
  const getAvailabilityStoresService = useGetAvailabilityStores();
  const mappingAdditionalOptions = useMappingAdditionalOptions();
  const mappingAdditionalOptionsActive = useConvertAdditionalOptionsActive();
  const mappingFreeGiftService = useMappingFreeGiftData();
  const boGetDataStatusReducer = useSelector((state) => state.buyingOptionReducer.BOGetDataStatusReducer);
  const boVariantsReducer = useSelector((state) => state.buyingOptionReducer.BOVariantsReducer);
  const freeGiftsList = useSelector(state => state.buyingOptionReducer.BOFreeGiftsReducer.freeGiftsList) || [];

  const freeGiftsSelectedID = useSelector(state => state.buyingOptionReducer.BOFreeGiftsReducer.freeGiftsSelectedID)
    || [];

  const additionalOptionsList = useSelector(state => state.buyingOptionReducer.BOAdditionalOptionsReducer.additionalOptionsList)
    || [];

  const additionalOptionsActive = useSelector(state => state.buyingOptionReducer.BOAdditionalOptionsReducer.additionalOptionsActive);

  const mappingPromotionTypeFreeGiftsList = useMemo(
    () => mappingFreeGiftService(freeGiftsList),
    [freeGiftsList, mappingFreeGiftService]
  );

  const mappingAdditionalOptionsActiveData = useMemo(
    () => mappingAdditionalOptions(mappingAdditionalOptionsActive(additionalOptionsActive)),
    [additionalOptionsActive, mappingAdditionalOptions]
  );

  return useCallback(
    /** @param {Array} allowedOptions
     * @param {Array} addOnDealProducts */
    async (allowedOptions, addOnDealProducts) => {
      let productsList = mappingProductsList([
        {
          ProductId: boGetDataStatusReducer.productId || '',
          Variants: boVariantsReducer.variantList || [],
          'Active Variant': {
            ...boVariantsReducer.variantActive,
            active: boVariantsReducer.variantSelectedLength ?? false,
            ProductId: boGetDataStatusReducer.productId || ''
          }
        }
      ]);

      const mappingAdditionalOptionsList = {};
      if (Object.keys(additionalOptionsList).length) {
        for (const [key, value] of Object.entries(additionalOptionsList)) {
          if (allowedOptions.find(item => item.optionId === key)?.required) {
            mappingAdditionalOptionsList[key] = {
              ...value
            };
          }
        }
      }

      if (addOnDealProducts?.length) {
        addOnDealProducts.forEach((aodItem) => productsList[0] = [
          ...productsList[0],
          {
            ...mappingProductsListPayload({
              productId: aodItem.ProductId || '',
              variantId: aodItem.VariantId || '',
              BOPISThreshold: aodItem.BOPISThreshold,
              lineQuantity: DEFAULT_PRODUCT_QUANTITY
            })
          }
        ]);
      }

      const isSelectedEnoughFreeGift = freeGiftsSelectedID?.length === freeGiftsList?.length;
      if (isSelectedEnoughFreeGift) {
        freeGiftsSelectedID.forEach((freeGiftItem) => productsList[0] = [
          ...productsList[0],
          {
            ...mappingProductsListPayload({
              productId: freeGiftItem.ProductId || '',
              variantId: freeGiftItem.VariantId || '',
              BOPISThreshold: freeGiftItem.BOPISThreshold,
              lineQuantity: DEFAULT_PRODUCT_QUANTITY
            })
          }
        ]);
      }

      let isSelectedEnoughAdditionalOptions = false;
      if (Object.keys(additionalOptionsList).length && allowedOptions.length) {
        const numberOfOptionsMustBeSelected = Object.keys(additionalOptionsList).reduce((count, optionKey) => {
          if (allowedOptions.find((optionItem) => optionItem.optionId === optionKey)?.required) count += 1;

          return count;
        }, 0);

        isSelectedEnoughAdditionalOptions =
          Object.keys(additionalOptionsActive).length >= numberOfOptionsMustBeSelected;

        if (isSelectedEnoughAdditionalOptions) {
          for (const [key, value] of Object.entries(additionalOptionsActive)) {
            const productData = additionalOptionsList?.[key]?.Products?.find(productItem => productItem?.ProductId
              === value);

            if (productData) {
              productsList[0] = [
                ...productsList[0],
                {
                  ...mappingProductsListPayload({
                    productId: productData.ProductId || '',
                    variantId: '',
                    BOPISThreshold: 0,
                    lineQuantity: DEFAULT_PRODUCT_QUANTITY
                  })
                }
              ];
            }
          }
        }
      }

      return await getAvailabilityStoresService(
        {
          isEnableBOPISOption: true,
          productsList,
          freeGiftsList: isSelectedEnoughFreeGift ? [] : mappingPromotionTypeFreeGiftsList,
          additionalOptions: isSelectedEnoughAdditionalOptions ? [] : mappingAdditionalOptions(
            mappingAdditionalOptionsList)
        },
        (
          (
            boVariantsReducer?.variantList?.length === 1 ? true : boVariantsReducer?.variantSelectedLength
          ) && (
            (
              freeGiftsList?.length ? isSelectedEnoughFreeGift : true
            ) || (
              Object.keys(additionalOptionsList)?.length ? isSelectedEnoughAdditionalOptions : true
            ) || addOnDealProducts.length
          )
        ) ? GET_AVAILABILITY_STORES_TYPE.And : GET_AVAILABILITY_STORES_TYPE.Or
      );
    }, [
      JSON.stringify(boGetDataStatusReducer),
      JSON.stringify(boVariantsReducer),
      JSON.stringify(freeGiftsSelectedID),
      JSON.stringify(mappingPromotionTypeFreeGiftsList),
      JSON.stringify(additionalOptionsList),
      JSON.stringify(freeGiftsList),
      JSON.stringify(additionalOptionsActive),
      JSON.stringify(mappingAdditionalOptionsActiveData),
      mappingAdditionalOptions
    ]);
};